import { Injectable } from '@angular/core';

export interface MembershipData {
  total: number;
  byType: {
    extraordinary: number;
    ordinary: number;
    lifetime: number;
    associate: number;
  };
  revenue: {
    newRegistration: number;
    renewal: number;
  };
}

export interface OrganizationData {
  total: number;
  byType: {
    appraisalCompany: number;
    bank: number;
    government: number;
    other: number;
  };
}

export interface AppraiserData {
  totalWithCard: number;
  totalWithoutCard: number;
  byType: {
    extraordinary: number;
    ordinary: number;
    senior: number;
    none: number;
  };
  revenue: {
    total: number;
    byType: {
      extraordinary: number;
      ordinary: number;
      senior: number;
    };
  };
}

export interface CourseData {
  totalCourses: number;
  totalCPD: number;
  revenue: number;
}

export interface PopularCourse {
  courseName: string;
  cpd: number;
  date: string;
  format: string;
  participants: number;
  revenue: number;
}

export interface ExamData {
  totalExams: number;
  byType: {
    extraordinary: number;
    ordinary: number;
    senior: number;
  };
}

export interface YearlyData {
  year: number;
  membership: MembershipData;
  organization: OrganizationData;
  appraiser: AppraiserData;
  course: CourseData;
  exam: ExamData;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardMockupService {
  private mockupData: Map<number, YearlyData> = new Map();

  constructor() {
    this.initializeMockupData();
  }

  private initializeMockupData(): void {
    // Year 2564 (2021)
    this.mockupData.set(2564, {
      year: 2564,
      membership: {
        total: 1850,
        byType: {
          extraordinary: 280,
          ordinary: 920,
          lifetime: 450,
          associate: 200
        },
        revenue: {
          newRegistration: 1850000,
          renewal: 2340000
        }
      },
      organization: {
        total: 320,
        byType: {
          appraisalCompany: 180,
          bank: 65,
          government: 45,
          other: 30
        }
      },
      appraiser: {
        totalWithCard: 1420,
        totalWithoutCard: 430,
        byType: {
          extraordinary: 220,
          ordinary: 780,
          senior: 420,
          none: 430
        },
        revenue: {
          total: 3560000,
          byType: {
            extraordinary: 880000,
            ordinary: 1560000,
            senior: 1120000
          }
        }
      },
      course: {
        totalCourses: 42,
        totalCPD: 504,
        revenue: 4200000
      },
      exam: {
        totalExams: 8,
        byType: {
          extraordinary: 2,
          ordinary: 4,
          senior: 2
        }
      }
    });

    // Year 2565 (2022)
    this.mockupData.set(2565, {
      year: 2565,
      membership: {
        total: 2150,
        byType: {
          extraordinary: 340,
          ordinary: 1050,
          lifetime: 520,
          associate: 240
        },
        revenue: {
          newRegistration: 2250000,
          renewal: 2890000
        }
      },
      organization: {
        total: 385,
        byType: {
          appraisalCompany: 215,
          bank: 78,
          government: 55,
          other: 37
        }
      },
      appraiser: {
        totalWithCard: 1680,
        totalWithoutCard: 470,
        byType: {
          extraordinary: 270,
          ordinary: 920,
          senior: 490,
          none: 470
        },
        revenue: {
          total: 4320000,
          byType: {
            extraordinary: 1080000,
            ordinary: 1840000,
            senior: 1400000
          }
        }
      },
      course: {
        totalCourses: 48,
        totalCPD: 624,
        revenue: 5100000
      },
      exam: {
        totalExams: 10,
        byType: {
          extraordinary: 3,
          ordinary: 5,
          senior: 2
        }
      }
    });

    // Year 2566 (2023)
    this.mockupData.set(2566, {
      year: 2566,
      membership: {
        total: 2480,
        byType: {
          extraordinary: 410,
          ordinary: 1180,
          lifetime: 610,
          associate: 280
        },
        revenue: {
          newRegistration: 2780000,
          renewal: 3450000
        }
      },
      organization: {
        total: 445,
        byType: {
          appraisalCompany: 248,
          bank: 92,
          government: 65,
          other: 40
        }
      },
      appraiser: {
        totalWithCard: 1950,
        totalWithoutCard: 530,
        byType: {
          extraordinary: 325,
          ordinary: 1050,
          senior: 575,
          none: 530
        },
        revenue: {
          total: 5180000,
          byType: {
            extraordinary: 1300000,
            ordinary: 2100000,
            senior: 1780000
          }
        }
      },
      course: {
        totalCourses: 56,
        totalCPD: 756,
        revenue: 6300000
      },
      exam: {
        totalExams: 12,
        byType: {
          extraordinary: 3,
          ordinary: 6,
          senior: 3
        }
      }
    });

    // Year 2567 (2024)
    this.mockupData.set(2567, {
      year: 2567,
      membership: {
        total: 2820,
        byType: {
          extraordinary: 485,
          ordinary: 1320,
          lifetime: 710,
          associate: 305
        },
        revenue: {
          newRegistration: 3250000,
          renewal: 4120000
        }
      },
      organization: {
        total: 512,
        byType: {
          appraisalCompany: 285,
          bank: 108,
          government: 75,
          other: 44
        }
      },
      appraiser: {
        totalWithCard: 2280,
        totalWithoutCard: 540,
        byType: {
          extraordinary: 385,
          ordinary: 1180,
          senior: 715,
          none: 540
        },
        revenue: {
          total: 6240000,
          byType: {
            extraordinary: 1540000,
            ordinary: 2360000,
            senior: 2340000
          }
        }
      },
      course: {
        totalCourses: 64,
        totalCPD: 896,
        revenue: 7800000
      },
      exam: {
        totalExams: 14,
        byType: {
          extraordinary: 4,
          ordinary: 7,
          senior: 3
        }
      }
    });

    // Year 2568 (2025)
    this.mockupData.set(2568, {
      year: 2568,
      membership: {
        total: 3180,
        byType: {
          extraordinary: 560,
          ordinary: 1480,
          lifetime: 820,
          associate: 320
        },
        revenue: {
          newRegistration: 3850000,
          renewal: 4890000
        }
      },
      organization: {
        total: 585,
        byType: {
          appraisalCompany: 325,
          bank: 125,
          government: 88,
          other: 47
        }
      },
      appraiser: {
        totalWithCard: 2620,
        totalWithoutCard: 560,
        byType: {
          extraordinary: 445,
          ordinary: 1320,
          senior: 855,
          none: 560
        },
        revenue: {
          total: 7480000,
          byType: {
            extraordinary: 1780000,
            ordinary: 2640000,
            senior: 3060000
          }
        }
      },
      course: {
        totalCourses: 72,
        totalCPD: 1008,
        revenue: 9200000
      },
      exam: {
        totalExams: 16,
        byType: {
          extraordinary: 5,
          ordinary: 8,
          senior: 3
        }
      }
    });

    // Year 2569 (2026) - Current Year
    this.mockupData.set(2569, {
      year: 2569,
      membership: {
        total: 3520,
        byType: {
          extraordinary: 640,
          ordinary: 1620,
          lifetime: 920,
          associate: 340
        },
        revenue: {
          newRegistration: 4450000,
          renewal: 5680000
        }
      },
      organization: {
        total: 658,
        byType: {
          appraisalCompany: 368,
          bank: 142,
          government: 98,
          other: 50
        }
      },
      appraiser: {
        totalWithCard: 2980,
        totalWithoutCard: 540,
        byType: {
          extraordinary: 510,
          ordinary: 1450,
          senior: 1020,
          none: 540
        },
        revenue: {
          total: 8920000,
          byType: {
            extraordinary: 2040000,
            ordinary: 2900000,
            senior: 3980000
          }
        }
      },
      course: {
        totalCourses: 78,
        totalCPD: 1092,
        revenue: 10800000
      },
      exam: {
        totalExams: 18,
        byType: {
          extraordinary: 6,
          ordinary: 9,
          senior: 3
        }
      }
    });
  }

  getDataByYear(year: number): YearlyData | undefined {
    return this.mockupData.get(year);
  }

  getAvailableYears(): number[] {
    return Array.from(this.mockupData.keys()).sort();
  }

  getCourseHistoricalData(): { year: number; courses: number; cpd: number; revenue: number }[] {
    const years = this.getAvailableYears();
    return years.map(year => {
      const data = this.mockupData.get(year)!;
      return {
        year,
        courses: data.course.totalCourses,
        cpd: data.course.totalCPD,
        revenue: data.course.revenue
      };
    });
  }

  getExamHistoricalData(): { year: number; exams: number }[] {
    const years = this.getAvailableYears();
    return years.map(year => {
      const data = this.mockupData.get(year)!;
      return {
        year,
        exams: data.exam.totalExams
      };
    });
  }

  getTopPopularCourses(): PopularCourse[] {
    return [
      {
        courseName: 'การประเมินมูลค่าอสังหาริมทรัพย์เชิงลึก',
        cpd: 24,
        date: '15-18 ม.ค. 2567',
        format: 'Onsite',
        participants: 156,
        revenue: 936000
      },
      {
        courseName: 'มาตรฐานการประเมินมูลค่าสากล IVSC',
        cpd: 18,
        date: '22-24 ก.พ. 2567',
        format: 'Hybrid',
        participants: 142,
        revenue: 852000
      },
      {
        courseName: 'การประเมินมูลค่าธุรกิจและทรัพย์สินทางปัญญา',
        cpd: 20,
        date: '10-12 มี.ค. 2567',
        format: 'Online',
        participants: 138,
        revenue: 690000
      },
      {
        courseName: 'เทคนิคการประเมินมูลค่าเครื่องจักรและอุปกรณ์',
        cpd: 16,
        date: '5-7 เม.ย. 2567',
        format: 'Onsite',
        participants: 128,
        revenue: 768000
      },
      {
        courseName: 'กฎหมายและจริยธรรมสำหรับผู้ประเมินมูลค่า',
        cpd: 12,
        date: '18-19 พ.ค. 2567',
        format: 'Online',
        participants: 124,
        revenue: 496000
      },
      {
        courseName: 'การวิเคราะห์ข้อมูลทางการเงินเพื่อการประเมินมูลค่า',
        cpd: 18,
        date: '8-10 มิ.ย. 2567',
        format: 'Hybrid',
        participants: 118,
        revenue: 708000
      },
      {
        courseName: 'เทคโนโลยีและนวัตกรรมในการประเมินมูลค่า',
        cpd: 14,
        date: '15-17 ก.ค. 2567',
        format: 'Online',
        participants: 115,
        revenue: 460000
      },
      {
        courseName: 'การประเมินมูลค่าโครงการพัฒนาอสังหาริมทรัพย์',
        cpd: 20,
        date: '22-24 ส.ค. 2567',
        format: 'Onsite',
        participants: 112,
        revenue: 672000
      },
      {
        courseName: 'การประเมินมูลค่าสินทรัพย์เพื่อการลงทุน',
        cpd: 16,
        date: '12-14 ก.ย. 2567',
        format: 'Hybrid',
        participants: 108,
        revenue: 648000
      },
      {
        courseName: 'ระเบียบวิธีวิจัยและสถิติสำหรับการประเมินมูลค่า',
        cpd: 15,
        date: '20-22 ต.ค. 2567',
        format: 'Online',
        participants: 105,
        revenue: 525000
      }
    ];
  }
}
